import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ApiClient } from '../api'
import { Icon } from '../components/Icon'
import { formatDateRange } from '../lib/dates'

// Archive — Tweak T2a sidebar page. Admin-only listing of every event
// with archived_at IS NOT NULL, ordered newest-archived first. Row
// click-through opens the standard EventDetail; T2b will render that
// view in read-only mode when event.archivedAt is set. For now the
// banner / disabled inputs aren't wired (deliberate T2a/T2b split),
// so admins can still mutate via the URL — that's a known limitation
// that T2b closes.

export function Archive() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['archived-events'],
    queryFn: ApiClient.archivedEvents
  })
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    if (!q) return data
    return data.filter((e) => e.name.toLowerCase().includes(q))
  }, [data, search])

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl mb-1">Archive</h1>
        <p className="text-ink-soft">
          Events that have been archived. The data is preserved for reference
          but archived events don't appear in default views and can't be
          edited.
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap bg-white border border-line rounded px-4 py-2.5">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search archived events…"
          className="px-3 py-1.5 text-sm bg-white border border-line rounded outline-none focus:border-green flex-1 min-w-[16rem]"
        />
        <div className="text-xs text-ink-soft font-mono">
          {filtered.length} of {data?.length ?? 0}
        </div>
      </div>

      {isLoading && <div className="text-ink-soft text-sm font-mono">Loading archive…</div>}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load archived events</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {data && (
        <div className="bg-white border border-line rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-ink-soft border-b border-line bg-parchment/60">
                <th className="px-4 py-2">Event</th>
                <th className="px-3 py-2 w-40">Dates</th>
                <th className="px-3 py-2 w-40">Archived</th>
                <th className="px-3 py-2 w-44">Archived by</th>
                <th className="px-3 py-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr
                  key={e.id}
                  className="border-b border-line last:border-b-0 hover:bg-parchment/50 transition-colors"
                >
                  <td className="px-4 py-2.5">
                    <Link to={`/events/${e.id}`} className="flex items-center gap-2.5">
                      <span
                        aria-hidden
                        className="inline-block w-1 h-7 rounded-full flex-shrink-0"
                        style={{ background: e.type.colourBorder }}
                      />
                      <span className="min-w-0">
                        <div className="font-semibold truncate">{e.name}</div>
                        <div
                          className="inline-flex items-center gap-1.5 mt-0.5 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded"
                          style={{ background: e.type.colourBg, color: e.type.colourText }}
                        >
                          {e.type.name}
                        </div>
                      </span>
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-ink-soft font-mono">
                    {formatDateRange(e.startDate, e.endDate)}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-ink-soft font-mono">
                    {formatArchivedAt(e.archivedAt)}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {e.archivedBy?.name ?? <span className="italic text-ink-soft">unknown</span>}
                  </td>
                  <td className="px-3 py-2.5 text-ink-soft">
                    <Link to={`/events/${e.id}`} aria-label={`Open ${e.name}`}>
                      <Icon name="chevronRight" size={14} />
                    </Link>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-sm italic text-ink-soft">
                    {data.length === 0
                      ? 'No archived events yet.'
                      : 'No archived events match the search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Compact archived-at format: "12 May 2026, 14:18".
function formatArchivedAt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
